/*
    Creation Time: 2021 - Nov - 20
    Created by:  (hamidrezakk)
    Maintainers:
       1.  HamidrezaKK (hamidrezakks@gmail.com)
    Auditor: HamidrezaKK
    Copyright Qlub_ 2022
*/

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { Button, CustomerContainer, Typography } from '@qlub-dev/qlub-kit';
import { RefreshRounded } from '@mui/icons-material';
import { useTranslation } from '../../hook/translations';
import { IURLTopology, QSRRouterModeEnum, useQlubRouter } from '../../hook/router';
import { IRestaurant, OrderModeEnum } from '../../repository/vendor';
import { Error } from '../../repository/common';
import MultiOrderIcon from '../Icons/MultiOrderIcon';
import ErrorIcon from '../Icons/ErrorIcon';
import NoOrderIcon from '../Icons/NoOrderIcon';
import ImageWrapper from '../ImageWrapper';
import getMenuLink, { linkHardRefreshHandler } from '../../utility/menuLink';

import styles from './index.module.scss';

enum ErrorTypeEnum {
    Server = 1,
    NoOrder = 2,
    MultiOrder = 3,
    ConcurrentPayment = 4,
}

export function formatTime(time: number) {
    if (time <= 9) {
        return `00:0${time}`;
    }
    return `00:${time}`;
}

export const isNoOrderErr = (err: any) => {
    return [400, 409, 404, 422].includes(err.code);
};

export const isMultiOrderErr = (err: any) => {
    return err.code === 422;
};

export const isConcurrentPaymentFullyErr = (err: any) => {
    return err.code === 412 && err.items === 'CONCURRENT_PAYMENTS';
};

export const isConcurrentPaymentPartiallyErr = (err: any) => {
    return err.code === 412 && err.items === 'CONCURRENT_PARTIAL_PAYMENTS';
};

interface IProps {
    url: IURLTopology;
    lang?: string;
    err: Error;
    onRetry: () => void;
    vendor?: IRestaurant;
    loading?: boolean;
}

export default function InvoiceError({ url, lang, err, onRetry, vendor, loading }: IProps) {
    const { t } = useTranslation('common');
    const [countdown, setCountdown] = useState(10);
    const [tries, setTries] = useState(0);
    const { getRouterParam } = useQlubRouter();

    useEffect(() => {
        if (countdown === 0 && tries < 9) {
            setCountdown(10);
        }
    }, [err]);

    useEffect(() => {
        const fn = setTimeout(() => {
            if (countdown > 0) {
                setCountdown((o) => o - 1);
            }
        }, 1000);
        return () => {
            clearTimeout(fn);
        };
    }, [url.id, url.slug, err, countdown, tries]);

    const tryAgainHandler = () => {
        setTries((o) => o + 1);
        onRetry();
    };

    const errorType: ErrorTypeEnum = useMemo(() => {
        if (isNoOrderErr(err)) {
            return ErrorTypeEnum.NoOrder;
        }
        if (isMultiOrderErr(err)) {
            return ErrorTypeEnum.MultiOrder;
        }
        if (isConcurrentPaymentFullyErr(err)) {
            return ErrorTypeEnum.ConcurrentPayment;
        }
        return ErrorTypeEnum.Server;
    }, [err]);

    const getImg = () => {
        switch (errorType) {
            case ErrorTypeEnum.NoOrder:
                return <NoOrderIcon />;
            case ErrorTypeEnum.ConcurrentPayment:
            case ErrorTypeEnum.MultiOrder:
                return <MultiOrderIcon />;
            case ErrorTypeEnum.Server:
            default:
                return <ErrorIcon />;
        }
    };

    const title = useMemo(() => {
        switch (errorType) {
            case ErrorTypeEnum.NoOrder:
                if (vendor?.orderMode === OrderModeEnum.Mixed) {
                    return t('Come back to pay after your food is served');
                }
                return t('Looks like you have not ordered yet');
            case ErrorTypeEnum.ConcurrentPayment:
                return t('You are trying to pay for a bill that is already paid for');
            case ErrorTypeEnum.MultiOrder:
            case ErrorTypeEnum.Server:
            default:
                return t('Something went wrong!');
        }
    }, [errorType, t]);

    const desc = useMemo(() => {
        switch (errorType) {
            case ErrorTypeEnum.NoOrder:
                if (vendor?.orderMode === OrderModeEnum.Mixed) {
                    return '';
                }
                return t('No order to pay!');
            case ErrorTypeEnum.ConcurrentPayment:
            case ErrorTypeEnum.MultiOrder:
                return t('Please ask waitstaff for help!');
            case ErrorTypeEnum.Server:
            default:
                return t('Oops!');
        }
    }, [errorType, t]);

    const withOrderNow = errorType === ErrorTypeEnum.NoOrder && vendor?.orderMode === OrderModeEnum.Mixed;

    const tryAgainContent = () => {
        if (errorType === ErrorTypeEnum.MultiOrder || tries >= 10) {
            return null;
        }

        return (
            <Button
                color="primary"
                variant={withOrderNow ? 'outlined' : 'contained'}
                fullWidth={!withOrderNow}
                startIcon={<RefreshRounded />}
                disabled={countdown > 0 || loading}
                onClick={tryAgainHandler}
            >
                {countdown === 0 ? t('Try Again') : t('Try again in {{time}}', { time: formatTime(countdown) })}
            </Button>
        );
    };

    const orderNowContent = () => {
        return (
            <Link
                href={getRouterParam('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr', {
                    qsr: QSRRouterModeEnum.Mixed,
                    source: 'errorPage',
                })}
            >
                <Button color="primary" variant="contained" fullWidth>
                    {t('Order now')}
                </Button>
            </Link>
        );
    };

    const checkMenuContent = () => {
        if (errorType === ErrorTypeEnum.Server) {
            return (
                <Link
                    href={getRouterParam('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]', {
                        ...(vendor?.orderMode === OrderModeEnum.Mixed ? { qsr: QSRRouterModeEnum.Mixed } : {}),
                        source: 'errorPage',
                    })}
                >
                    <Button color="primary" variant="outlined" fullWidth>
                        {t('back to homepage')}
                    </Button>
                </Link>
            );
        }

        if (!vendor?.config.hideViewMenuButton) {
            const menuLink = getMenuLink(url, lang, 'errorPage');
            return (
                <Link href={menuLink} onClick={linkHardRefreshHandler(menuLink)}>
                    <Button color="primary" variant="outlined" fullWidth>
                        {t('Check Menu')}
                    </Button>
                </Link>
            );
        }

        return null;
    };

    const getContent = () => {
        if (withOrderNow) {
            return (
                <>
                    {orderNowContent()}
                    <div className={styles.row}>
                        {tryAgainContent()}
                        {checkMenuContent()}
                    </div>
                </>
            );
        }
        return (
            <>
                {tryAgainContent()}
                {checkMenuContent()}
            </>
        );
    };

    // TODO: fix margin and padding
    return (
        <CustomerContainer fixed padding="0 12px" margin="0 auto" maxWidth="sm">
            <div className={styles.frame}>
                <div className={styles.image}>
                    <ImageWrapper>{getImg()}</ImageWrapper>
                </div>
                {title && (
                    <Typography component="h1" className={styles.title}>
                        {title}
                    </Typography>
                )}
                {desc && <div className={styles.desc}>{desc}</div>}
                <div className={styles.buttons}>{getContent()}</div>
            </div>
        </CustomerContainer>
    );
}
